// src/components/Questionnaires/GroupProjectWorkPartB.js

import React, {useEffect, useState} from 'react';
import {
    Card,
    Form,
    Radio,
    Checkbox,
    Input,
    Button,
    Alert,
    Typography,
    message,
    Spin
} from 'antd';
import moment from 'moment';
import "./GroupProjectWorkPartB.css";

const {Title, Text} = Typography;
const {TextArea} = Input;

// Mapping between simplified keys and full question strings
const questionKeyMap = {
    // Part 1: Project Setup and Planning
    subject: "What is the subject(s) covered in this project? (e.g., Science, Mathematics, History, etc.)",
    curricularGoals: "What are the key curricular goals of this project? (What are you expected to achieve through this project?)",
    competencies: "What competencies will this project help you develop? (e.g., Communication skills, Problem-solving, Creativity, Collaboration, etc.)",
    problemStatement: "What is the main problem statement or challenge the project addresses? (State the central question or purpose of the project.)",
    finalOutput: "What is the planned final output of this project? (e.g., Report, Presentation, Prototype, Model, etc.)",

    // Part 1.B: Pedagogies and Guiding Questions
    teachingApproaches: "Which teaching-learning approaches are integrated into this project? (Tick all that apply):",
    teachingApproachesOther: "If yes, please specify:",
    guidingQuestions: "List the guiding questions that will help you explore the project topic: (e.g., What are the causes of the problem? What solutions can we explore?)",
    priorKnowledge: "What do you already know about the topic? (Summarize your prior knowledge.)",
    knowledgeGaps: "What do you need to find out or research further? (List the gaps in your knowledge.)",

    // Part 2: Scheduling and Resource Planning
    projectTimeline: "What is the timeline or schedule for completing this project? (List tasks by days or weeks, e.g., Day 1: Research, Day 2: Drafting, etc.)",
    requiredResources: "What resources do you need to complete this project? (e.g., Books, Internet, Tools, Materials, etc.)",
    groupRoles: "What roles have been assigned to each group member? (e.g., Leader, Researcher, Presenter, etc.)",
    potentialBarriers: "What potential barriers might arise during this project? How will you overcome them?",

    // Part 3.A: Learner Reflection
    learnerAwareness: "Awareness: (Tick all that apply)",
    learnerSensitivity: "Sensitivity: (Tick all that apply)",
    learnerCreativity: "Creativity: (Tick all that apply)",

    // Part 3.B: Teacher Assessment
    teacherAwareness: "Awareness: (Tick all that apply) - Teacher",
    teacherSensitivity: "Sensitivity: (Tick all that apply) - Teacher",
    teacherCreativity: "Creativity: (Tick all that apply) - Teacher",

    // Part 4.A: Performance Rubric
    performanceAwareness: "awareness",
    performanceSensitivity: "sensitivity",
    performanceCreativity: "creativity",

    // Part 4.B: Learner Reflection
    projectLearning: "What did you learn from this project?",
    enjoyablePart: "What was the most enjoyable part of the project?",
    projectChallenges: "What were the challenges you faced during the project? How did you overcome them?",
    keyStrengths: "What are your three key strengths demonstrated in this project?",
    improvementAreas: "What are two areas you identified for improvement?",
    remainingQuestions: "What questions do you still have about the project?",

    // Part 4.C: Peer Feedback
    peerContribution: "How well did your peer(s) contribute to the project? (Tick all that apply):",

    // Part 5: Overview and Summary
    consolidatedAwareness: "awareness",
    consolidatedSensitivity: "sensitivity",
    consolidatedCreativity: "creativity",
    teacherComments: "Teacher Comments:",
};

const GroupProjectWorkPartB = ({server_url, accessToken}) => {
    const [form] = Form.useForm();
    const [isEligible, setIsEligible] = useState(true);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [latestSubmission, setLatestSubmission] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch eligibility and latest submission on component mount
    useEffect(() => {
        const checkEligibility = async () => {
            try {
                // Check Eligibility
                const eligibilityResponse = await fetch(`${server_url}/questionnaires/group-project-work-partB/eligibility`, {
                    method: 'GET',
                    headers: {Authorization: `Bearer ${accessToken}`},
                });

                if (!eligibilityResponse.ok) {
                    throw new Error('Failed to check eligibility.');
                }

                const eligibilityData = await eligibilityResponse.json();
                setIsEligible(eligibilityData.eligible);
                setEligibilityMessage(eligibilityData.message);

                if (eligibilityData.eligible) {
                    // Fetch the latest submission
                    const latestResponse = await fetch(`${server_url}/questionnaires/group-project-work-partB/latest`, {
                        method: 'GET',
                        headers: {Authorization: `Bearer ${accessToken}`},
                    });

                    if (!latestResponse.ok) {
                        throw new Error('Failed to fetch latest submission.');
                    }

                    const latestData = await latestResponse.json();
                    if (latestData.latest_submission) {
                        setLatestSubmission(latestData.latest_submission);

                        // Map full questions to simplified keys
                        const mappedValues = {};
                        for (const [question, answer] of Object.entries(latestData.latest_submission.qna)) {
                            const key = Object.keys(questionKeyMap).find(k => questionKeyMap[k] === question);
                            if (key) {
                                mappedValues[key] = answer;
                            }
                        }

                        form.setFieldsValue(mappedValues);
                    }
                }
            } catch (err) {
                console.error(err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        checkEligibility();
    }, [server_url, accessToken, form]);

    // Handle form submission
    const handleSubmit = async (values) => {
        console.log('Submitting form values:', values);
        setIsSubmitting(true);

        // Map simplified keys to full question strings and handle array to string conversion
        const answers = {};
        for (const [key, value] of Object.entries(values)) {
            if (questionKeyMap[key]) {
                // If the value is an array (from checkboxes), join it into a comma-separated string
                answers[questionKeyMap[key]] = Array.isArray(value) ? value.join(', ') : value;
            }
        }

        try {
            const response = await fetch(`${server_url}/questionnaires/group-project-work-partB/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify({
                    form_data: {
                        answers: answers,
                    },
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                // Stringify the error detail for better readability
                const errorDetail = typeof errorData.detail === 'object' ? JSON.stringify(errorData.detail) : errorData.detail;
                throw new Error(errorDetail || 'Failed to submit Group Project Work Part B.');
            }

            const data = await response.json();
            message.success('Group Project Work Part B submitted successfully!');
            setIsEligible(false);
            setEligibilityMessage(`Next eligible date: ${moment(data.next_eligible_date).format('YYYY-MM-DD')}`);
            // Optionally, reset the form or keep the latest submission displayed
        } catch (err) {
            console.error('Error submitting Group Project Work Part B:', err);
            message.error(err.message || 'An error occurred while submitting the form.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return <Spin tip="Loading..."/>;
    }

    if (error) {
        return <Alert message={error} type="error" showIcon/>;
    }

    return (
        <Card>
            <div className="main-container">
                <div className="bp-group-project">
                    <h2 className="bp-header">PART B - Group Project Work</h2>

                    <div className="bp-subsection">
                        <h3>
                            Subject(s) <span className="bp-span-small">(Can be more than one)</span>
                        </h3>
                        <textarea className="bp-textarea-40"></textarea>
                    </div>

                    <div className="bp-subsection">
                        <h3>
                            Curricular goal(s) <span className="bp-span-small">(Can be more than one)</span>
                        </h3>
                        <textarea className="bp-textarea-40"></textarea>
                    </div>

                    <div className="bp-subsection">
                        <h3>
                            Competency(ies)
                            <span className="bp-span-small">(Can be more than one)</span>
                        </h3>
                        <textarea className="bp-textarea-40"></textarea>
                    </div>

                    <div className="bp-pedagogies">
                        <h3>
                            Pedagogies <span className="bp-span-small">(Tick ✓ all that apply.)</span>
                        </h3>
                        <label className="bp-pedagogy-label" onClick="toggleCheckbox(this)">
                            <input type="checkbox" className="bp-hidden-checkbox"/> Art-integrated
                        </label>
                        <label className="bp-pedagogy-label" onClick="toggleCheckbox(this)">
                            <input type="checkbox" className="bp-hidden-checkbox"/> Toy-based
                        </label>
                        <label className="bp-pedagogy-label" onClick="toggleCheckbox(this)">
                            <input type="checkbox" className="bp-hidden-checkbox"/> Skill-based learning
                        </label>
                        <label className="bp-pedagogy-label" onClick="toggleCheckbox(this)">
                            <input type="checkbox" className="bp-hidden-checkbox"/> Indian Knowledge Systems approaches
                        </label>
                        <label className="bp-pedagogy-label" onClick="toggleCheckbox(this)">
                            <input type="checkbox" className="bp-hidden-checkbox"/> Sports-integrated
                        </label>
                        <label className="bp-pedagogy-label" onClick="toggleCheckbox(this)">
                            <input type="checkbox" className="bp-hidden-checkbox"/> Technology-integrated
                        </label>
                        <label className="bp-pedagogy-label" onClick="toggleCheckbox(this)">
                            <input type="checkbox" className="bp-hidden-checkbox"/> Drama/Theatre-integrated
                        </label>
                        <label className="bp-pedagogy-label" onClick="toggleCheckbox(this)">
                            <input type="checkbox" className="bp-hidden-checkbox"/>
                            Any other:
                            <input type="text" className="bp-input-text"/>
                        </label>
                    </div>

                    <div className="bp-prompt">
                        <h3>Project prompt/question/problem/challenge/planned final output</h3>
                        <textarea className="bp-textarea-60"></textarea>
                    </div>

                    <div className="bp-guiding">
                        <h3>Guiding Questions</h3>
                        <textarea className="bp-textarea-60"></textarea>
                    </div>

                    <div className="bp-flex-two">
                        <div className="bp-box">
                            <h3>What do I know?</h3>
                            <textarea className="bp-textarea-80"></textarea>
                        </div>
                        <div className="bp-box">
                            <h3>What do I need to find out?</h3>
                            <textarea className="bp-textarea-80"></textarea>
                        </div>
                    </div>
                </div>

                <div className="s1-schedule">
                    <h2 className="s1-header">Stage 1</h2>
                    <div className="s1-schedule-title">
                        <h3>Project Schedule</h3>
                        <p>
                            Day 1 | Day 2 | Day 3 | Day 4 | Day 5 | Day 6 | Day 7 | Day 8 | Day 9 | Day
                            10
                        </p>
                    </div>
                    <div className="s1-flex-three">
                        <div className="s1-resources">
                            <h3>Resources Needed</h3>
                            <textarea className="s1-textarea-100" placeholder="List resources here..."></textarea>
                        </div>
                        <div className="s1-roles">
                            <h3>Roles of Group Members</h3>
                            <textarea className="s1-textarea-100"></textarea>
                        </div>
                        <div className="s1-barriers">
                            <h3>Possible Barriers to Doing the Project</h3>
                            <textarea className="s1-textarea-100"></textarea>
                        </div>
                    </div>
                    <div className="s1-learner-reflection-title">
                        <h3>Learner Reflection</h3>
                        <p><strong>Tick (✓) statements that apply.</strong></p>
                    </div>
                    <div className="s1-awareness">
                        <h3>Awareness</h3>
                        <label><input type="checkbox"/> I understand the purpose of the project.</label
                        ><br/>
                        <label><input type="checkbox"/> I could read and understand the resource material.</label
                        ><br/>
                        <label><input type="checkbox"/> I talk about things I know that are needed for the
                            project.</label
                        ><br/>
                        <label><input type="checkbox"/> I identify challenges my group might face during the
                            project.</label
                        ><br/>
                        <label><input type="checkbox"/> I could enumerate and describe the steps (start to finish)
                            required to do the project.</label
                        ><br/>
                        <p>
                            <strong>Total number of (✓):</strong>
                            <input type="text" className="s1-input-number"/>
                        </p>
                    </div>
                    <div className="s1-sensitivity">
                        <h3>Sensitivity</h3>
                        <label><input type="checkbox"/> I listen to my group’s ideas and respect them.</label
                        ><br/>
                        <label><input type="checkbox"/> I try to make sure group decisions are taken
                            collectively.</label
                        ><br/>
                        <label><input type="checkbox"/> I try to make sure that my peers understand all aspects of the
                            project.</label
                        ><br/>
                        <label><input type="checkbox"/> I can meaningfully relate to the objectives of the
                            project.</label
                        ><br/>
                        <label><input type="checkbox"/> I feel joyous in contributing to the project.</label
                        ><br/>
                        <p>
                            <strong>Total number of (✓):</strong>
                            <input type="text" className="s1-input-number"/>
                        </p>
                    </div>
                    <div className="s1-creativity">
                        <h3>Creativity</h3>
                        <label><input type="checkbox"/> I think of different ways to approach the task.</label
                        ><br/>
                        <label><input type="checkbox"/> I brainstorm about project execution and presentation.</label
                        ><br/>
                        <label><input type="checkbox"/> I think of different resources to be used in the project.</label
                        ><br/>
                        <label><input type="checkbox"/> I come up with innovative solutions to mitigate the challenges.</label
                        ><br/>
                        <label><input type="checkbox"/> I can think of new ideas to relate the output of the project in
                            daily
                            life.</label
                        ><br/>
                        <p>
                            <strong>Total number of (✓):</strong>
                            <input type="text" className="s1-input-number"/>
                        </p>
                    </div>
                </div>


                <div className="s1-teacher-assessment">
                    <h3>Stage 1 Teacher Assessment</h3>
                    <p><strong>Tick (✓) statements that apply.</strong></p>
                </div>

                <div className="s1-awareness-teacher">
                    <h3>Awareness</h3>
                    <label><input type="checkbox"/> Guiding questions created by the learner demonstrate a clear
                        understanding of project goals and objectives.</label><br/>
                    <label><input type="checkbox"/> The learner identifies potential challenges and proposes
                        solutions.</label><br/>
                    <label><input type="checkbox"/> The learner develops a plan for project execution.</label><br/>
                    <label><input type="checkbox"/> The learner can clearly communicate project ideas and plans to
                        other group members.</label><br/>
                    <label><input type="checkbox"/> The learner can successfully identify existing content knowledge
                        and gaps in their understanding.</label><br/>
                    <p>
                        <strong>Total number of (✓):</strong>
                        <input type="text" className="s1-total-input"/>
                    </p>
                </div>

                <div className="s1-sensitivity-teacher">
                    <h3>Sensitivity</h3>
                    <label><input type="checkbox"/> The learner actively seeks input from all group members during
                        planning.</label><br/>
                    <label><input type="checkbox"/> The learner tries to support fair distribution of tasks and
                        responsibilities including all genders.</label><br/>
                    <label><input type="checkbox"/> The learner ensures that decisions are made
                        collaboratively.</label><br/>
                    <label><input type="checkbox"/> The learner actively maintains a shared understanding with the
                        group.</label><br/>
                    <label><input type="checkbox"/> The learner can handle different opinions in the group
                        respectfully.</label><br/>
                    <p>
                        <strong>Total number of (✓):</strong>
                        <input type="text" className="s1-total-input"/>
                    </p>
                </div>

                <div className="s1-creativity-teacher">
                    <h3>Creativity</h3>
                    <label><input type="checkbox"/> The learner is able to brainstorm about project execution and
                        presentation.</label><br/>
                    <label><input type="checkbox"/> The learner demonstrates curiosity in proposing sources of
                        material beyond conventional resources.</label><br/>
                    <label><input type="checkbox"/> The learner demonstrates initiative in proposing solutions to
                        possible barriers.</label><br/>
                    <label><input type="checkbox"/> The learner can provide creative input to decide the roles of
                        group members.</label><br/>
                    <label><input type="checkbox"/> The learner can go beyond the defined features of the project
                        prompt/task and add a unique element to it.</label><br/>
                    <p>
                        <strong>Total number of (✓):</strong>
                        <input type="text" className="s1-total-input"/>
                    </p>
                </div>

                <div className="s1-comments">
                    <h3>Brief comments and pedagogical interventions recommended:</h3>
                    <textarea className="s1-comments-textarea"></textarea>
                </div>

                <div className="s2-container">
                    <div className="s2-teacher-assessment-title">
                        <h2>Stage 2</h2>
                        <h3>Teacher Assessment</h3>
                        <p><strong>Tick (✓) statements that apply.</strong></p>
                    </div>
                    <div className="s2-awareness">
                        <h3>Awareness</h3>
                        <label><input type="checkbox"/> The learner shows sufficient engagement in the project
                            work.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner presents a draft as per the project schedule.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner demonstrates thorough research skills on the
                            project.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner identifies possible areas of improvement in the
                            draft.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner is aware of team members’ contributions.</label
                        ><br/>
                        <label><input type="checkbox"/> The product demonstrates application of knowledge gained.</label
                        ><br/>
                        <p>
                            <strong>Total number of (✓):</strong>
                            <input type="text" className="s2-input-number"/>
                        </p>
                    </div>
                    <div className="s2-sensitivity">
                        <h3>Sensitivity</h3>
                        <label><input type="checkbox"/> The learner actively seeks input from all group members.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner supports fair distribution of tasks.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner ensures decisions are made collaboratively.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner maintains a shared understanding with the
                            group.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner handles different opinions respectfully.</label
                        ><br/>
                        <p>
                            <strong>Total number of (✓):</strong>
                            <input type="text" className="s2-input-number"/>
                        </p>
                    </div>
                    <div className="s2-creativity">
                        <h3>Creativity</h3>
                        <label><input type="checkbox"/> The learner is flexible with respect to project roles.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner considers different sources of information.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner takes initiative to complete tasks.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner builds on unique elements introduced earlier.</label
                        ><br/>
                        <label><input type="checkbox"/> The learner selects ideas from the brainstorming stage.</label
                        ><br/>
                        <label><input type="checkbox"/> The product is innovative and useful to the community.</label
                        ><br/>
                        <p>
                            <strong>Total number of (✓):</strong>
                            <input type="text" className="s2-input-number"/>
                        </p>
                    </div>
                    <div className="s2-comments">
                        <h3>Brief comments and pedagogical interventions recommended:</h3>
                        <textarea className="s2-textarea-80"></textarea>
                    </div>
                </div>

                <div className="s3-rubric">
                    <h2 className="s3-header">Stage 3</h2>
                    <p className="s3-rubric-description">
                        Based on the contents of the final output of the project, write a rubric for each
                        ability and achievement level. Tick (✓) as per the learner’s performance.
                    </p>
                    <table className="s3-table">
                        <tr className="s3-table-header">
                            <th>Beginner <br/>(5)</th>
                            <th>Proficient <br/>(10)</th>
                            <th>Advanced <br/>(15)</th>
                        </tr>
                        <tr className="s3-rubric-section">
                            <th colSpan="3">Awareness</th>
                        </tr>
                        <tr>
                            <td className="s3-table-cell"><input type="checkbox"/></td>
                            <td className="s3-table-cell"><input type="checkbox"/></td>
                            <td className="s3-table-cell"><input type="checkbox"/></td>
                        </tr>
                        <tr className="s3-rubric-section">
                            <th colSpan="3">Sensitivity</th>
                        </tr>
                        <tr>
                            <td className="s3-table-cell"><input type="checkbox"/></td>
                            <td className="s3-table-cell"><input type="checkbox"/></td>
                            <td className="s3-table-cell"><input type="checkbox"/></td>
                        </tr>
                        <tr className="s3-rubric-section">
                            <th colSpan="3">Creativity</th>
                        </tr>
                        <tr>
                            <td className="s3-table-cell"><input type="checkbox"/></td>
                            <td className="s3-table-cell"><input type="checkbox"/></td>
                            <td className="s3-table-cell"><input type="checkbox"/></td>
                        </tr>
                    </table>
                </div>

                <h2 className="stage3-header">Stage 3</h2>

                <div className="stage3-learner-reflection">
                    <h3 className="stage3-learner-reflection-title">Learner Reflection</h3>
                    <p className="stage3-learner-reflection-desc"><strong>Tick (✓) statements that apply.</strong></p>
                </div>

                <div className="stage3-awareness">
                    <h3 className="stage3-awareness-title">Awareness</h3>
                    <label className="stage3-label"><input type="checkbox"/> I could identify areas where my
                        understanding of the project has improved.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> I could explain how my work contributed to
                        the overall project.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> I was able to improve the project based on
                        my own and my peers’ review.</label><br/>
                    <p className="stage3-total"><strong>Total number of (✓):</strong> <input type="text"
                                                                                             className="stage3-total-input"/>
                    </p>
                </div>

                <div className="stage3-sensitivity">
                    <h3 className="stage3-sensitivity-title">Sensitivity</h3>
                    <label className="stage3-label"><input type="checkbox"/> I was able to build a positive emotional
                        atmosphere within the group.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> I could reflect on my strengths and areas
                        for improvement.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> I could understand the social relevance of
                        the project.</label><br/>
                    <p className="stage3-total"><strong>Total number of (✓):</strong> <input type="text"
                                                                                             className="stage3-total-input"/>
                    </p>
                </div>

                <div className="stage3-creativity">
                    <h3 className="stage3-creativity-title">Creativity</h3>
                    <label className="stage3-label"><input type="checkbox"/> I was able to make creative contributions
                        to the project.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> I was able to take initiative to complete
                        the project.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> I was able to use different materials,
                        tools, and resources during the project.</label><br/>
                    <p className="stage3-total"><strong>Total number of (✓):</strong> <input type="text"
                                                                                             className="stage3-total-input"/>
                    </p>
                </div>

                <div className="stage3-peer-feedback">
                    <h3 className="stage3-peer-feedback-title">Peer Feedback</h3>
                    <p className="stage3-peer-feedback-desc"><strong>Tick (✓) all statements that apply.</strong></p>
                </div>

                <div className="stage3-peer-awareness">
                    <h3 className="stage3-peer-awareness-title">Awareness</h3>
                    <label className="stage3-label"><input type="checkbox"/> My peer showed improved understanding of
                        the project.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> My peer could explain how her/his work
                        contributed to the overall project.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> My peer was able to improve the project
                        based on her/his own and their peers’ review.</label><br/>
                    <p className="stage3-total"><strong>Total number of (✓):</strong> <input type="text"
                                                                                             className="stage3-total-input"/>
                    </p>
                </div>

                <div className="stage3-peer-sensitivity">
                    <h3 className="stage3-peer-sensitivity-title">Sensitivity</h3>
                    <label className="stage3-label"><input type="checkbox"/> My peer helped to build a positive
                        emotional atmosphere within the group by valuing everyone’s opinions.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> My peer could reflect on her/his strengths
                        and areas for improvement.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> My peer could understand the social
                        relevance of the project.</label><br/>
                    <p className="stage3-total"><strong>Total number of (✓):</strong> <input type="text"
                                                                                             className="stage3-total-input"/>
                    </p>
                </div>

                <div className="stage3-peer-creativity">
                    <h3 className="stage3-peer-creativity-title">Creativity</h3>
                    <label className="stage3-label"><input type="checkbox"/> My peer was able to make creative
                        contributions to the project.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> My peer was able to take the initiative to
                        help complete the project.</label><br/>
                    <label className="stage3-label"><input type="checkbox"/> My peer was able to use different
                        materials, tools, and resources during the project.</label><br/>
                    <p className="stage3-total"><strong>Total number of (✓):</strong> <input type="text"
                                                                                             className="stage3-total-input"/>
                    </p>
                </div>

                <div className="overview-container">
                    <h2 className="overview-header">Overview - TO IMPROVISE</h2>
                    <p className="overview-description">
                        Tabulate all the feedback for the group project on this page.
                    </p>
                    <div className="overview-teacher-title">
                        <h3>Teacher Assessment</h3>
                    </div>
                    <table className="overview-teacher-table">
                        <tr className="overview-table-header">
                            <th>Teacher</th>
                            <th>Awareness</th>
                            <th>Sensitivity</th>
                            <th>Creativity</th>
                        </tr>
                        <tr>
                            <td>Stage 1</td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                        </tr>
                        <tr>
                            <td>Stage 2</td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                        </tr>
                        <tr>
                            <td>Stage 3</td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                        </tr>
                        <tr>
                            <td><strong>Total</strong></td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                        </tr>
                    </table>

                    <div className="overview-learner-title">
                        <h3>Learner Reflection</h3>
                    </div>
                    <table className="overview-learner-table">
                        <tr className="overview-table-header">
                            <th>Learner</th>
                            <th>Awareness</th>
                            <th>Sensitivity</th>
                            <th>Creativity</th>
                        </tr>
                        <tr>
                            <td>Stage 1</td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                        </tr>
                        <tr>
                            <td>Stage 3</td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                        </tr>
                    </table>

                    <div className="overview-peer-title">
                        <h3>Peer Feedback</h3>
                        <p><strong>Tick (✓) all statements that apply.</strong></p>
                    </div>
                    <table className="overview-peer-table">
                        <tr className="overview-table-header">
                            <th>Peer</th>
                            <th>Awareness</th>
                            <th>Sensitivity</th>
                            <th>Creativity</th>
                        </tr>
                        <tr>
                            <td>Stage 3</td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                            <td><input type="text" className="overview-input"/></td>
                        </tr>
                    </table>

                    <div className="overview-final">
                        <h3>Final Performance Level</h3>
                        <p>Select the final performance level based on the total score:</p>
                        <label><input type="radio" name="performance"/> Beginner (0-11)</label><br/>
                        <label><input type="radio" name="performance"/> Proficient (12-18)</label><br/>
                        <label><input type="radio" name="performance"/> Advanced (19-26)</label>
                    </div>
                </div>


            </div>
            {/* Eligibility Message */}
            {/*<Alert*/}
            {/*    message={eligibilityMessage}*/}
            {/*    type={isEligible ? "success" : "warning"}*/}
            {/*    showIcon*/}
            {/*    style={{marginBottom: 16}}*/}
            {/*/>*/}

            {/*/!* If Eligible, Show the Form *!/*/}
            {/*{isEligible ? (*/}
            {/*    <Form*/}
            {/*        layout="vertical"*/}
            {/*        form={form}*/}
            {/*        onFinish={handleSubmit}*/}
            {/*        onFinishFailed={(errorInfo) => {*/}
            {/*            console.log('Failed:', errorInfo);*/}
            {/*            message.error('Please complete all required fields.');*/}
            {/*        }}*/}
            {/*    >*/}
            {/*        /!* Part 1: Project Setup and Planning *!/*/}
            {/*        <Title level={4}>Part 1: Project Setup and Planning</Title>*/}

            {/*        /!* A. General Information *!/*/}
            {/*        <Title level={5}>A. General Information</Title>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.subject}*/}
            {/*            name="subject"*/}
            {/*            rules={[{required: true, message: 'Please enter the subject(s) covered.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Science, Mathematics, History, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.curricularGoals}*/}
            {/*            name="curricularGoals"*/}
            {/*            rules={[{required: true, message: 'Please describe the curricular goals.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the curricular goals."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.competencies}*/}
            {/*            name="competencies"*/}
            {/*            rules={[{required: true, message: 'Please list the competencies.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="e.g., Communication skills, Problem-solving, Creativity, Collaboration, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.problemStatement}*/}
            {/*            name="problemStatement"*/}
            {/*            rules={[{required: true, message: 'Please state the problem statement or challenge.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="State the central question or purpose of the project."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.finalOutput}*/}
            {/*            name="finalOutput"*/}
            {/*            rules={[{required: true, message: 'Please describe the planned final output.'}]}*/}
            {/*        >*/}
            {/*            <Input placeholder="e.g., Report, Presentation, Prototype, Model, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* B. Pedagogies and Guiding Questions *!/*/}
            {/*        <Title level={5}>B. Pedagogies and Guiding Questions</Title>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.teachingApproaches}*/}
            {/*            name="teachingApproaches"*/}
            {/*            rules={[{required: true, message: 'Please select at least one approach.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="Art-integrated">Art-integrated</Checkbox>*/}
            {/*                <Checkbox value="Sports-integrated">Sports-integrated</Checkbox>*/}
            {/*                <Checkbox value="Technology-integrated">Technology-integrated</Checkbox>*/}
            {/*                <Checkbox value="Drama/Theatre-integrated">Drama/Theatre-integrated</Checkbox>*/}
            {/*                <Checkbox value="Skill-based learning">Skill-based learning</Checkbox>*/}
            {/*                <Checkbox value="Toy-based learning">Toy-based learning</Checkbox>*/}
            {/*                <Checkbox value="Indian Knowledge Systems approaches">Indian Knowledge Systems*/}
            {/*                    approaches</Checkbox>*/}
            {/*                <Checkbox value="Any other">Any other</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Conditional Field: Any other *!/*/}
            {/*        <Form.Item*/}
            {/*            shouldUpdate={(prevValues, currentValues) =>*/}
            {/*                prevValues.teachingApproaches !== currentValues.teachingApproaches*/}
            {/*            }*/}
            {/*        >*/}
            {/*            {({getFieldValue}) => {*/}
            {/*                const selected = getFieldValue("teachingApproaches");*/}
            {/*                return selected && selected.includes("Any other") ? (*/}
            {/*                    <Form.Item*/}
            {/*                        label={questionKeyMap.teachingApproachesOther}*/}
            {/*                        name="teachingApproachesOther"*/}
            {/*                        rules={[{required: true, message: 'Please specify the other approaches.'}]}*/}
            {/*                    >*/}
            {/*                        <Input placeholder="Specify other teaching-learning approaches."/>*/}
            {/*                    </Form.Item>*/}
            {/*                ) : null;*/}
            {/*            }}*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.guidingQuestions}*/}
            {/*            name="guidingQuestions"*/}
            {/*            rules={[{required: true, message: 'Please list the guiding questions.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3}*/}
            {/*                      placeholder="e.g., What are the causes of the problem? What solutions can we explore?"/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.priorKnowledge}*/}
            {/*            name="priorKnowledge"*/}
            {/*            rules={[{required: true, message: 'Please summarize your prior knowledge.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Summarize your prior knowledge."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.knowledgeGaps}*/}
            {/*            name="knowledgeGaps"*/}
            {/*            rules={[{required: true, message: 'Please list the gaps in your knowledge.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="List the gaps in your knowledge."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 2: Scheduling and Resource Planning *!/*/}
            {/*        <Title level={4}>Part 2: Scheduling and Resource Planning</Title>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.projectTimeline}*/}
            {/*            name="projectTimeline"*/}
            {/*            rules={[{required: true, message: 'Please provide the project timeline.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={4}*/}
            {/*                      placeholder="List tasks by days or weeks, e.g., Day 1: Research, Day 2: Drafting, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.requiredResources}*/}
            {/*            name="requiredResources"*/}
            {/*            rules={[{required: true, message: 'Please list the required resources.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="e.g., Books, Internet, Tools, Materials, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.groupRoles}*/}
            {/*            name="groupRoles"*/}
            {/*            rules={[{required: true, message: 'Please assign roles to each group member.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="e.g., Leader, Researcher, Presenter, etc."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.potentialBarriers}*/}
            {/*            name="potentialBarriers"*/}
            {/*            rules={[{required: true, message: 'Please describe potential barriers and solutions.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={4} placeholder="Barriers: ________________ Solutions: ________________"/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 3: Reflection and Assessment (Stage 1 & 2) *!/*/}
            {/*        <Title level={4}>Part 3: Reflection and Assessment (Stage 1 & 2)</Title>*/}

            {/*        /!* A. Learner Reflection *!/*/}
            {/*        <Title level={5}>A. Learner Reflection</Title>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.learnerAwareness}*/}
            {/*            name="learnerAwareness"*/}
            {/*            rules={[{required: true, message: 'Please select at least one option.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="I understand the purpose of the project.">I understand the purpose of the*/}
            {/*                    project.</Checkbox>*/}
            {/*                <Checkbox value="I could read and understand the resource material.">I could read and*/}
            {/*                    understand the resource material.</Checkbox>*/}
            {/*                <Checkbox value="I talked about things I know that are needed for the project.">I talked*/}
            {/*                    about things I know that are needed for the project.</Checkbox>*/}
            {/*                <Checkbox value="I identified challenges my group might face during the project.">I*/}
            {/*                    identified challenges my group might face during the project.</Checkbox>*/}
            {/*                <Checkbox value="I could describe the steps needed to complete the project.">I could*/}
            {/*                    describe the steps needed to complete the project.</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.learnerSensitivity}*/}
            {/*            name="learnerSensitivity"*/}
            {/*            rules={[{required: true, message: 'Please select at least one option.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="I listened to my group’s ideas and respected them.">I listened to my*/}
            {/*                    group’s ideas and respected them.</Checkbox>*/}
            {/*                <Checkbox value="I made sure group decisions were taken collectively.">I made sure group*/}
            {/*                    decisions were taken collectively.</Checkbox>*/}
            {/*                <Checkbox value="I ensured my peers understood all aspects of the project.">I ensured my*/}
            {/*                    peers understood all aspects of the project.</Checkbox>*/}
            {/*                <Checkbox value="I meaningfully related to the project objectives.">I meaningfully related*/}
            {/*                    to the project objectives.</Checkbox>*/}
            {/*                <Checkbox value="I felt happy contributing to the project.">I felt happy contributing to the*/}
            {/*                    project.</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.learnerCreativity}*/}
            {/*            name="learnerCreativity"*/}
            {/*            rules={[{required: true, message: 'Please select at least one option.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="I thought of different ways to approach the task.">I thought of different*/}
            {/*                    ways to approach the task.</Checkbox>*/}
            {/*                <Checkbox value="I brainstormed about project execution and presentation.">I brainstormed*/}
            {/*                    about project execution and presentation.</Checkbox>*/}
            {/*                <Checkbox value="I proposed innovative solutions to challenges.">I proposed innovative*/}
            {/*                    solutions to challenges.</Checkbox>*/}
            {/*                <Checkbox value="I used creative ideas to relate the output to daily life.">I used creative*/}
            {/*                    ideas to relate the output to daily life.</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* B. Teacher Assessment *!/*/}
            {/*        <Title level={5}>B. Teacher Assessment</Title>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.teacherAwareness}*/}
            {/*            name="teacherAwareness"*/}
            {/*            rules={[{required: true, message: 'Please select at least one option.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox*/}
            {/*                    value="The learner demonstrates a clear understanding of project goals and objectives.">The*/}
            {/*                    learner demonstrates a clear understanding of project goals and objectives.</Checkbox>*/}
            {/*                <Checkbox value="The learner identifies potential challenges and proposes solutions.">The*/}
            {/*                    learner identifies potential challenges and proposes solutions.</Checkbox>*/}
            {/*                <Checkbox value="The learner can clearly communicate project ideas and plans to the group.">The*/}
            {/*                    learner can clearly communicate project ideas and plans to the group.</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.teacherSensitivity}*/}
            {/*            name="teacherSensitivity"*/}
            {/*            rules={[{required: true, message: 'Please select at least one option.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="The learner seeks input from all group members.">The learner seeks input*/}
            {/*                    from all group members.</Checkbox>*/}
            {/*                <Checkbox value="The learner ensures decisions are made collaboratively.">The learner*/}
            {/*                    ensures decisions are made collaboratively.</Checkbox>*/}
            {/*                <Checkbox value="The learner handles different opinions in the group respectfully.">The*/}
            {/*                    learner handles different opinions in the group respectfully.</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.teacherCreativity}*/}
            {/*            name="teacherCreativity"*/}
            {/*            rules={[{required: true, message: 'Please select at least one option.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="The learner proposes innovative solutions.">The learner proposes innovative*/}
            {/*                    solutions.</Checkbox>*/}
            {/*                <Checkbox value="The learner effectively uses diverse resources and tools.">The learner*/}
            {/*                    effectively uses diverse resources and tools.</Checkbox>*/}
            {/*                <Checkbox value="The learner adds unique and creative elements to the project.">The learner*/}
            {/*                    adds unique and creative elements to the project.</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 4: Final Evaluation (Stage 3) *!/*/}
            {/*        <Title level={4}>Part 4: Final Evaluation (Stage 3)</Title>*/}

            {/*        /!* A. Performance Rubric *!/*/}
            {/*        <Title level={5}>A. Performance Rubric</Title>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.performanceAwareness}*/}
            {/*            name="performanceAwareness"*/}
            {/*            rules={[{required: true, message: 'Please provide ratings for awareness.'}]}*/}
            {/*        >*/}
            {/*            <div>*/}
            {/*                <Text strong>Awareness:</Text>*/}
            {/*                <Radio.Group>*/}
            {/*                    <Radio value="Beginner (5)">Beginner (5)</Radio>*/}
            {/*                    <Radio value="Proficient (10)">Proficient (10)</Radio>*/}
            {/*                    <Radio value="Advanced (15)">Advanced (15)</Radio>*/}
            {/*                </Radio.Group>*/}
            {/*            </div>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.performanceSensitivity}*/}
            {/*            name="performanceSensitivity"*/}
            {/*            rules={[{required: true, message: 'Please provide ratings for sensitivity.'}]}*/}
            {/*        >*/}
            {/*            <div>*/}
            {/*                <Text strong>Sensitivity:</Text>*/}
            {/*                <Radio.Group>*/}
            {/*                    <Radio value="Beginner (5)">Beginner (5)</Radio>*/}
            {/*                    <Radio value="Proficient (10)">Proficient (10)</Radio>*/}
            {/*                    <Radio value="Advanced (15)">Advanced (15)</Radio>*/}
            {/*                </Radio.Group>*/}
            {/*            </div>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.performanceCreativity}*/}
            {/*            name="performanceCreativity"*/}
            {/*            rules={[{required: true, message: 'Please provide ratings for creativity.'}]}*/}
            {/*        >*/}
            {/*            <div>*/}
            {/*                <Text strong>Creativity:</Text>*/}
            {/*                <Radio.Group>*/}
            {/*                    <Radio value="Beginner (5)">Beginner (5)</Radio>*/}
            {/*                    <Radio value="Proficient (10)">Proficient (10)</Radio>*/}
            {/*                    <Radio value="Advanced (15)">Advanced (15)</Radio>*/}
            {/*                </Radio.Group>*/}
            {/*            </div>*/}
            {/*        </Form.Item>*/}

            {/*        /!* B. Learner Reflection *!/*/}
            {/*        <Title level={5}>B. Learner Reflection</Title>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.projectLearning}*/}
            {/*            name="projectLearning"*/}
            {/*            rules={[{required: true, message: 'Please share your learning.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Share what you learned from this project."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.enjoyablePart}*/}
            {/*            name="enjoyablePart"*/}
            {/*            rules={[{required: true, message: 'Please describe the enjoyable part.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Describe the most enjoyable part of the project."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.projectChallenges}*/}
            {/*            name="projectChallenges"*/}
            {/*            rules={[{required: true, message: 'Please describe the challenges and solutions.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={3} placeholder="Describe the challenges and how you overcame them."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.keyStrengths}*/}
            {/*            name="keyStrengths"*/}
            {/*            rules={[{required: true, message: 'Please list your key strengths.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="List your three key strengths."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.improvementAreas}*/}
            {/*            name="improvementAreas"*/}
            {/*            rules={[{required: true, message: 'Please list areas for improvement.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="List two areas for improvement."/>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.remainingQuestions}*/}
            {/*            name="remainingQuestions"*/}
            {/*            rules={[{required: true, message: 'Please share your remaining questions.'}]}*/}
            {/*        >*/}
            {/*            <TextArea rows={2} placeholder="Share your remaining questions about the project."/>*/}
            {/*        </Form.Item>*/}

            {/*        /!* C. Peer Feedback *!/*/}
            {/*        <Title level={5}>C. Peer Feedback</Title>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.peerContribution}*/}
            {/*            name="peerContribution"*/}
            {/*            rules={[{required: true, message: 'Please select at least one option.'}]}*/}
            {/*        >*/}
            {/*            <Checkbox.Group>*/}
            {/*                <Checkbox value="My peer improved understanding of the project.">My peer improved*/}
            {/*                    understanding of the project.</Checkbox>*/}
            {/*                <Checkbox value="My peer explained their contributions clearly.">My peer explained their*/}
            {/*                    contributions clearly.</Checkbox>*/}
            {/*                <Checkbox value="My peer took initiative to help complete the project.">My peer took*/}
            {/*                    initiative to help complete the project.</Checkbox>*/}
            {/*                <Checkbox value="My peer helped build a positive emotional atmosphere.">My peer helped build*/}
            {/*                    a positive emotional atmosphere.</Checkbox>*/}
            {/*            </Checkbox.Group>*/}
            {/*        </Form.Item>*/}

            {/*        /!* Part 5: Overview and Summary *!/*/}
            {/*        <Title level={4}>Part 5: Overview and Summary</Title>*/}

            {/*        <Form.Item*/}
            {/*            label="Final Scores and Levels (Consolidated):"*/}
            {/*            style={{marginBottom: 16}}*/}
            {/*        >*/}
            {/*            <div style={{marginBottom: 16}}>*/}
            {/*                /!* Awareness *!/*/}
            {/*                <Text strong>Awareness:</Text>*/}
            {/*                <Form.Item*/}
            {/*                    name="consolidatedAwareness"*/}
            {/*                    noStyle*/}
            {/*                    rules={[{required: true, message: 'Please select awareness level.'}]}*/}
            {/*                >*/}
            {/*                    <Radio.Group>*/}
            {/*                        <Radio value="Beginner">Beginner</Radio>*/}
            {/*                        <Radio value="Proficient">Proficient</Radio>*/}
            {/*                        <Radio value="Advanced">Advanced</Radio>*/}
            {/*                    </Radio.Group>*/}
            {/*                </Form.Item>*/}
            {/*            </div>*/}

            {/*            <div style={{marginBottom: 16}}>*/}
            {/*                /!* Sensitivity *!/*/}
            {/*                <Text strong>Sensitivity:</Text>*/}
            {/*                <Form.Item*/}
            {/*                    name="consolidatedSensitivity"*/}
            {/*                    noStyle*/}
            {/*                    rules={[{required: true, message: 'Please select sensitivity level.'}]}*/}
            {/*                >*/}
            {/*                    <Radio.Group>*/}
            {/*                        <Radio value="Beginner">Beginner</Radio>*/}
            {/*                        <Radio value="Proficient">Proficient</Radio>*/}
            {/*                        <Radio value="Advanced">Advanced</Radio>*/}
            {/*                    </Radio.Group>*/}
            {/*                </Form.Item>*/}
            {/*            </div>*/}

            {/*            <div style={{marginBottom: 16}}>*/}
            {/*                /!* Creativity *!/*/}
            {/*                <Text strong>Creativity:</Text>*/}
            {/*                <Form.Item*/}
            {/*                    name="consolidatedCreativity"*/}
            {/*                    noStyle*/}
            {/*                    rules={[{required: true, message: 'Please select creativity level.'}]}*/}
            {/*                >*/}
            {/*                    <Radio.Group>*/}
            {/*                        <Radio value="Beginner">Beginner</Radio>*/}
            {/*                        <Radio value="Proficient">Proficient</Radio>*/}
            {/*                        <Radio value="Advanced">Advanced</Radio>*/}
            {/*                    </Radio.Group>*/}
            {/*                </Form.Item>*/}
            {/*            </div>*/}
            {/*        </Form.Item>*/}

            {/*        <Form.Item*/}
            {/*            label={questionKeyMap.teacherComments}*/}
            {/*            name="teacherComments"*/}
            {/*            rules={[{required: true, message: 'Please provide teacher comments.'}]}*/}
            {/*        >*/}
            {/*            <TextArea*/}
            {/*                rows={3}*/}
            {/*                placeholder="The learner’s most important contribution was: ________________ To improve in future projects, the learner should focus on: ________________"*/}
            {/*            />*/}
            {/*        </Form.Item>*/}

            {/*        /!* Submit Button *!/*/}
            {/*        <Form.Item>*/}
            {/*            <Button type="primary" className="bg-yellow-500 hover:bg-yellow-600 text-black" htmlType="submit" loading={isSubmitting}>*/}
            {/*                Submit*/}
            {/*            </Button>*/}
            {/*        </Form.Item>*/}
            {/*    </Form>*/}
            {/*) : (*/}
            {/*    <Text type="warning">*/}
            {/*        You can only submit this form once every 15 days. Please come back later.*/}
            {/*    </Text>*/}
            {/*)}*/}

            {/* Display Latest Submission */}
            {latestSubmission && isEligible && (
                <Card title="Your Latest Submission" style={{marginTop: 24}}>
                    {Object.entries(latestSubmission.qna).map(([question, answer], index) => (
                        <div key={index} style={{marginBottom: 16}}>
                            <Text strong>{question}</Text>
                            <br/>
                            <Text>{Array.isArray(answer) ? answer.join(', ') : answer}</Text>
                        </div>
                    ))}
                </Card>
            )}
        </Card>
    );

};

export default GroupProjectWorkPartB;
